import type {
  ElementStyle,
  RelationshipStyle as RelStyle,
  Configuration,
} from '../types'

/**
 * Default C4 colors from the C4 model specification
 */
export const C4_COLORS = {
  person: {
    internal: { background: '#08427b', stroke: '#052e56', color: '#ffffff' },
    external: { background: '#999999', stroke: '#6b6b6b', color: '#ffffff' },
  },
  softwareSystem: {
    internal: { background: '#1168bd', stroke: '#0b4884', color: '#ffffff' },
    external: { background: '#999999', stroke: '#6b6b6b', color: '#ffffff' },
  },
  container: {
    default: { background: '#438dd5', stroke: '#2e6295', color: '#ffffff' },
  },
  component: {
    default: { background: '#85bbf0', stroke: '#5d99c6', color: '#000000' },
  },
  relationship: {
    default: { color: '#707070', dashed: true },
    synchronous: { color: '#707070', dashed: false },
    asynchronous: { color: '#707070', dashed: true },
  },
}

export interface ResolvedElementStyle {
  background?: string
  stroke?: string
  color?: string
  shape?: string
  width?: number
  height?: number
  border?: string
  opacity?: number
}

export interface ResolvedRelationshipStyle {
  color?: string
  dashed?: boolean
  thickness?: number
  routing?: string
  sequenceNumber?: string
}

/**
 * Parse tags string into array
 */
export function parseTags(tags?: string): string[] {
  if (!tags) return []
  return tags.split(',').map(t => t.trim()).filter(Boolean)
}

/**
 * Check if an element has a specific tag
 */
export function hasTag(tags: string[] | string | undefined, tag: string): boolean {
  const tagList = Array.isArray(tags) ? tags : parseTags(tags)
  return tagList.some(t => t.toLowerCase() === tag.toLowerCase())
}

/**
 * Resolve styles for an element based on its tags
 */
export function resolveElementStyle(
  tags: string[] | string | undefined,
  elementType: 'Person' | 'SoftwareSystem' | 'Container' | 'Component',
  configuration?: Configuration,
  location?: 'Internal' | 'External' | 'Unspecified'
): ResolvedElementStyle {
  const tagList = Array.isArray(tags) ? tags : parseTags(tags)

  // Start with default colors based on element type
  let style: ResolvedElementStyle = {}

  const isExternal = location === 'External' || hasTag(tagList, 'External')

  switch (elementType) {
    case 'Person':
      style = isExternal
        ? { ...C4_COLORS.person.external }
        : { ...C4_COLORS.person.internal }
      break
    case 'SoftwareSystem':
      style = isExternal
        ? { ...C4_COLORS.softwareSystem.external }
        : { ...C4_COLORS.softwareSystem.internal }
      break
    case 'Container':
      style = { ...C4_COLORS.container.default }
      break
    case 'Component':
      style = { ...C4_COLORS.component.default }
      break
  }

  // Check for Database tag to use cylinder shape
  if (hasTag(tagList, 'Database')) {
    style.shape = 'Cylinder'
  }

  // Apply custom styles from configuration
  if (configuration?.styles?.elements) {
    for (const elementStyle of configuration.styles.elements) {
      // Check if this style applies to any of the element's tags
      if (hasTag(tagList, elementStyle.tag) || elementStyle.tag === 'Element') {
        applyElementStyle(style, elementStyle)
      }
    }
  }

  return style
}

/**
 * Apply an element style definition to the resolved style
 */
function applyElementStyle(
  style: ResolvedElementStyle,
  elementStyle: ElementStyle
): void {
  if (elementStyle.background) style.background = elementStyle.background
  if (elementStyle.stroke) style.stroke = elementStyle.stroke
  if (elementStyle.color) style.color = elementStyle.color
  if (elementStyle.shape) style.shape = elementStyle.shape
  if (elementStyle.width) style.width = elementStyle.width
  if (elementStyle.height) style.height = elementStyle.height
  if (elementStyle.border) style.border = elementStyle.border
  if (elementStyle.opacity !== undefined) style.opacity = elementStyle.opacity
}

/**
 * Resolve styles for a relationship based on its tags
 */
export function resolveRelationshipStyle(
  tags: string[] | string | undefined,
  interactionStyle?: 'Synchronous' | 'Asynchronous',
  configuration?: Configuration
): ResolvedRelationshipStyle {
  const tagList = Array.isArray(tags) ? tags : parseTags(tags)

  // Start with default relationship style
  let style: ResolvedRelationshipStyle = {
    ...C4_COLORS.relationship.default,
  }

  // Apply interaction style
  if (interactionStyle === 'Synchronous') {
    style.dashed = false
  } else if (interactionStyle === 'Asynchronous') {
    style.dashed = true
  }

  // Apply custom styles from configuration
  if (configuration?.styles?.relationships) {
    for (const relStyle of configuration.styles.relationships) {
      // Check if this style applies to any of the relationship's tags
      if (hasTag(tagList, relStyle.tag) || relStyle.tag === 'Relationship') {
        applyRelationshipStyle(style, relStyle)
      }
    }
  }

  return style
}

/**
 * Apply a relationship style definition to the resolved style
 */
function applyRelationshipStyle(
  style: ResolvedRelationshipStyle,
  relStyle: RelStyle
): void {
  if (relStyle.color) style.color = relStyle.color
  if (relStyle.dashed !== undefined) style.dashed = relStyle.dashed
  if (relStyle.thickness) style.thickness = relStyle.thickness
  if (relStyle.routing) style.routing = relStyle.routing
}

/**
 * Normalize shape name from Structurizr format to our internal shape type
 */
function normalizeShapeName(shape: string): string {
  // Map Structurizr shape names to our shape types
  const shapeMap: Record<string, string> = {
    'box': 'RoundedBox',
    'roundedbox': 'RoundedBox',
    'circle': 'Circle',
    'ellipse': 'Ellipse',
    'hexagon': 'Hexagon',
    'cylinder': 'Cylinder',
    'pipe': 'Pipe',
    'person': 'Person',
    'robot': 'Robot',
    'folder': 'Folder',
    'webbrowser': 'WebBrowser',
    'mobiledeviceportrait': 'MobileDevicePortrait',
    'mobiledevicelandscape': 'MobileDeviceLandscape',
    'component': 'Component',
    'diamond': 'Diamond',
  }

  const normalized = shapeMap[shape.toLowerCase()]
  return normalized || shape
}

/**
 * Determine the shape to use for an element based on tags and styles
 */
export function determineShape(
  tags: string[] | string | undefined,
  elementType: 'Person' | 'SoftwareSystem' | 'Container' | 'Component',
  configuration?: Configuration
): string {
  const style = resolveElementStyle(tags, elementType, configuration)

  if (style.shape) {
    return normalizeShapeName(style.shape)
  }

  // Default shapes based on type
  switch (elementType) {
    case 'Person':
      return 'Person'
    case 'SoftwareSystem':
      return 'RoundedBox'
    case 'Container':
      // Check for database tag
      const tagList = Array.isArray(tags) ? tags : parseTags(tags)
      if (hasTag(tagList, 'Database')) {
        return 'Cylinder'
      }
      return 'RoundedBox'
    case 'Component':
      return 'Component'
    default:
      return 'RoundedBox'
  }
}
